import { Link } from "@tanstack/react-router";
import { Users, Heart, Scale, Award, ShieldCheck, Unlock } from "lucide-react";

/**
 * Positive positioning grid: 6 reasons Colorado owners pick us.
 */
export function BoutiqueAdvantage({
  eyebrow = "Why Colorado Owners Pick Us",
  title,
  className = "",
}: {
  eyebrow?: string;
  title?: React.ReactNode;
  className?: string;
}) {
  const points = [
    {
      icon: Users,
      t: "You work directly with the builder.",
      d: "The owner is on every project — strategy, design, code, SEO. No salesperson handing you to an account manager handing you to a builder. One expert, start to finish, and you have their direct line.",
      tag: "Owner-led",
    },
    {
      icon: Heart,
      t: "A real Colorado family business.",
      d: "Small, family-run Colorado Springs shop. Personalized, custom service and decisions made in one conversation — not three Slack threads and a status meeting. The people on your project actually care that it works.",
      tag: "Family-run",
      to: "/how-we-work",
      cta: "See our process →",
    },
    {
      icon: Scale,
      t: "Premium work at a Colorado price.",
      d: "Lean office, no bloated headquarters, AI in our daily workflow, and we serve Colorado only — so we never charge you for overhead you'll never use. Same in-house craftsmanship as the big shops, priced for real Colorado businesses.",
      tag: "Smart pricing",
    },
    {
      icon: Award,
      t: "Two decades in the Colorado trenches.",
      d: "Chris Heidlebaugh founded the shop in Colorado Springs in 2006. Hundreds of launches, every Front Range market, every common industry. The owner doing your work has done it a thousand times — for businesses that look a lot like yours.",
      tag: "20 years deep",
      to: "/meet-chris",
      cta: "Meet Chris →",
    },
    {
      icon: ShieldCheck,
      t: "One name on the work.",
      d: "No ticket queue, no \"your account manager will get back to you.\" You have the owner's direct line, and one person owns the outcome. Same throat to choke, same hand to shake — usually answered the same day.",
      tag: "Direct line",
    },
    {
      icon: Unlock,
      t: "Month-to-month — you own everything.",
      d: "No long contracts, no early-termination fees, no hostage platforms. Site, domain, hosting, analytics — all yours from day one. We earn next month with last month's results, the way it should be.",
      tag: "Earn it monthly",
    },
  ];

  return (
    <section className={`bg-background py-20 lg:py-24 ${className}`}>
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-gold-deep">
            {eyebrow}
          </span>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
            {title ?? (
              <>
                Built to be <span className="text-gradient-gold">the best partner</span> a Colorado business can hire.
              </>
            )}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Two decades in Colorado Springs. Hundreds of launches across the Front Range. A small, family-run team where the owner does the work, picks up the phone, and stays on every project from kickoff to ranking. Here's what you actually get when you hire us.
          </p>
        </div>


        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {points.map((p) => (
            <div
              key={p.t}
              className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/50 hover:shadow-card-hover"
            >
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-gold text-brand-black shadow-gold">
                <p.icon className="h-6 w-6" />
              </div>
              <div className="mt-5 text-[10px] font-bold uppercase tracking-widest text-brand-gold-deep">
                {p.tag}
              </div>
              <h3 className="mt-1.5 text-lg font-extrabold leading-snug tracking-tight text-foreground">
                {p.t}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                {p.d}
              </p>
              {p.to && p.cta && (
                <Link
                  to={p.to}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand-gold-deep transition-smooth hover:text-brand-gold"
                >
                  {p.cta}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
